import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';

import { DokumenteService, FileUpload, FileUploadResponse } from '../services/dokumente.service';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { AuthQuery } from '@app/auth/auth.query';

@Component({
  selector: 'app-dokumente',
  templateUrl: './dokumente.component.html',
  styleUrls: ['./dokumente.component.scss']
})
export class DokumenteComponent implements OnInit, OnChanges {
  @Input() tableName: string = 'standort'; // Default to standort
  @Input() recordId: string;
  @Input() clientId?: string; // when provided, enables client aggregation mode
  @Input() isClientView: boolean = false; // when true, aggregate all standort files for client
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'filename',
    'type_size',
    'uploaded_by',
    'upload_date',
    'actions'
  ];

  dataSource: MatTableDataSource<FileUpload>;
  isLoadingResults = true;
  isRateLimitReached = false;
  totalItems = 0;
  pageSizes = [10, 25, 50, 100];
  currentPageSize = 10;
  currentPageIndex = 0;
  searchValue = '';
  groupedDokumente: { [key: string]: FileUpload[] } = {};

  // FilePond
  @ViewChild('myPond') myPond: any;
  get pondOptions() {
    // Base options
    const baseOptions: any = {
      class: 'my-filepond',
      credits: false,
      multiple: true,
      maxFiles: 10,
      allowFileTypeValidation: true,
      allowImageValidateSize: true,
      imageValidateSizeMinWidth: 150,
      imageValidateSizeMinHeight: 150,
      allowImageResize: true,
      imageResizeTargetWidth: 2400,
      imageResizeTargetHeight: 1600,
      imageResizeMode: 'contain',
      imageResizeUpscale: true,
      labelIdle: 'Dateien hier ablegen oder <a style="text-decoration:underline;">Durchsuchen</a> ...',
      server: {
      process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
        const formData = new FormData();
        formData.append(fieldName, file, file.name);
        formData.append('filepond', file.name);
        formData.append('table_name', this.tableName);
        formData.append('record_id', this.recordId);

        // Get user info from session to include in form data
        const sessionKey = 'smarthcaap_webapp_auth_session_v1';
        const session = localStorage.getItem(sessionKey);
        let token: string | null = null;
        let userId: string | number | null = null;
        let userName: string | null = null;
        
        if (session) {
          try {
            const sessionData = JSON.parse(session);
            token = sessionData.access_token;
            
            // Try to get user info from session data first (more reliable)
            userId = sessionData.userid || sessionData.uid || null;
            userName = sessionData.name || sessionData.username || sessionData.dienstname || null;
            
            // If not in session, try to extract from JWT token
            if (!userId && token) {
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                userId = payload?.uid ?? payload?.userid ?? null;
                userName = payload?.username ?? payload?.name ?? null;
              } catch (e) {
                console.warn('FilePond upload: Could not parse JWT token payload:', e);
              }
            }
            
            // Add user info to form data if available
            if (userId && userId !== 'undefined' && userId !== 'null') {
              formData.append('uid', String(userId));
              console.log('FilePond upload: Setting user ID:', userId);
            } else {
              console.warn('FilePond upload: No valid user ID found');
            }
            
            if (userName && userName !== 'undefined' && userName !== 'null') {
              formData.append('uname', String(userName));
              console.log('FilePond upload: Setting user name:', userName);
            } else {
              console.warn('FilePond upload: No valid user name found');
            }
          } catch (e) {
            console.error('FilePond upload: Error parsing session data:', e);
          }
        } else {
          console.warn('FilePond upload: No session found in localStorage');
        }

        const request = new XMLHttpRequest();
        request.open('POST', environment.apiUrl + '/filepond/process');

        // Add JWT token to request header (token already extracted above)
        console.log('FilePond upload - Session key:', sessionKey);
        console.log('FilePond upload - Session exists:', !!session);
        console.log('FilePond upload - Token exists:', !!token);
        console.log('FilePond upload - User ID:', userId);
        console.log('FilePond upload - User name:', userName);
        
        if (token) {
          request.setRequestHeader('Authorization', 'Bearer ' + token);
          console.log('FilePond upload - JWT token added:', token.substring(0, 20) + '...');
          console.log('FilePond upload - Authorization header set: Bearer ' + token.substring(0, 20) + '...');
        } else {
          console.warn('FilePond upload - No JWT token found in session data');
          console.warn('FilePond upload - Upload will fail with 401 Unauthorized');
          // Don't proceed with upload if no token
          error('Sie müssen sich zuerst anmelden, um Dateien hochzuladen.');
          return;
        }

        // Remove CORS headers - they should be set by the server, not the client
        // request.setRequestHeader('Access-Control-Allow-Origin', '*');
        // request.setRequestHeader('Access-Control-Allow-Headers', '*');

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = function () {
          console.log('FilePond upload - Response status:', request.status);
          console.log('FilePond upload - Response headers:', request.getAllResponseHeaders());
          console.log('FilePond upload - Response text:', request.responseText);
          
          if (request.status >= 200 && request.status < 300) {
            console.log('FilePond upload - Success, loading response');
            load(request.responseText);
          } else {
            console.error('FilePond upload - Failed with status:', request.status);
            console.error('FilePond upload - Error response:', request.responseText);
            error('Upload failed: ' + request.status + ' - ' + request.responseText);
          }
        };

        request.onerror = function () {
          console.error('FilePond upload network error');
          error('Network error during upload');
        };

        request.send(formData);

        return {
          abort: () => {
            request.abort();
            abort();
          },
        };
      },
      revert: 'filepond/revert',
      restore: 'filepond/restore?id=',
      fetch: 'filepond/fetch?data=',
      load: 'filepond/load',
      },
    };

    // Restrict to JPG/PNG only in Plan Dateien context
    if (this.isPlanDateienContext) {
      baseOptions.acceptedFileTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      baseOptions.labelFileTypeNotAllowed = 'Nur JPG und PNG Dateien werden unterstützt';
    } else {
      baseOptions.acceptedFileTypes = ['image/*', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain'];
      baseOptions.labelFileTypeNotAllowed = 'Dateityp nicht erlaubt';
    }

    return baseOptions;
  }

  pondFiles = [];

  constructor(
    private dokumenteService: DokumenteService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private authQuery: AuthQuery
  ) {
    this.dataSource = new MatTableDataSource<FileUpload>([]);
  }

  // Check if user is Administrator/Superadmin (role 1) or Firmen-Administrator (role 2)
  get isCompAdmin(): boolean {
    return this.authQuery.role === 1 || this.authQuery.role === 2;
  }

  // Check if we're in the Plan Dateien context (standort-edit, Plan Dateien tab)
  get isPlanDateienContext(): boolean {
    return this.isEmbedded && 
           this.parentContext === 'standort-edit' && 
           this.tableName === 'standort';
  }

  ngOnInit() {
    console.log('DokumenteComponent: ngOnInit called with tableName:', this.tableName, 'recordId:', this.recordId);
    console.log('🔍 Component Context:', {
      isEmbedded: this.isEmbedded,
      parentContext: this.parentContext
    });
    
    // Debug JWT token
    this.checkJwtToken();
    
    if (this.isEmbedded) {
      this.showEmbeddedUI();
    } else {
      this.showStandaloneUI();
    }
    
    // Inject standort column dynamically for client aggregated view
    if (this.isClientView && !this.displayedColumns.includes('standort')) {
      this.displayedColumns = ['filename', 'standort', 'type_size', 'uploaded_by', 'upload_date', 'actions'];
    }
    this.loadDokumente();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['recordId'] && !changes['recordId'].firstChange) {
      console.log('DokumenteComponent: recordId changed from', changes['recordId'].previousValue, 'to', changes['recordId'].currentValue);
      this.loadDokumente();
    }
  }

  ngAfterViewInit() {
    if (this.paginator && this.sort) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      
      // Initialize paginator with current state
      this.paginator.pageIndex = this.currentPageIndex;
      this.paginator.pageSize = this.currentPageSize;
      
      // Set default sort by filename A-Z
      if (!this.sort.active) {
        this.sort.active = 'original_filename';
        this.sort.direction = 'asc';
        this.dataSource.sort = this.sort;
      }
    }
  }

  loadDokumente() {
    // Client aggregation mode: require clientId
    if (this.isClientView) {
      if (!this.clientId) {
        console.warn('No clientId provided for client documents aggregation');
        this.dataSource.data = [];
        this.totalItems = 0;
        return;
      }
      console.log('DokumenteComponent: Loading client aggregated documents for clientId:', this.clientId);
    } else {
      if (!this.recordId) {
        console.warn('No record ID provided for documents loading');
        return;
      }
      console.log('DokumenteComponent: Loading documents for tableName:', this.tableName, 'recordId:', this.recordId);
    }
    this.isLoadingResults = true;
    
    // Use stored pagination state or fallback to defaults
    const page = this.currentPageIndex + 1;
    const limit = this.currentPageSize;
    const sortBy = this.sort ? this.sort.active : 'filename';
    const sortOrder = this.sort ? this.sort.direction : 'asc';
    
    // Use server-side search if searchValue is provided
    const searchTerm = this.searchValue && this.searchValue.trim().length > 0 ? this.searchValue.trim() : undefined;

    const obs = this.isClientView && this.clientId
      ? this.dokumenteService.getClientDokumente(this.clientId, page, limit, searchTerm)
      : this.dokumenteService.getDokumente(this.tableName, this.recordId, page, limit, searchTerm);

    obs.subscribe(
      (response: FileUploadResponse) => {
        if (this.isClientView && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Handle grouped data for client view
          this.groupedDokumente = response.data as { [key: string]: FileUpload[] };
          // Flatten grouped data for table display
          const flatData: FileUpload[] = [];
          Object.keys(this.groupedDokumente).forEach(standortName => {
            flatData.push(...this.groupedDokumente[standortName]);
          });
          // Sort by filename A-Z
          flatData.sort((a, b) => {
            const filenameA = (a.original_filename || a.filename || '').toLowerCase();
            const filenameB = (b.original_filename || b.filename || '').toLowerCase();
            return filenameA.localeCompare(filenameB, 'de', { numeric: true, sensitivity: 'base' });
          });
          this.dataSource.data = flatData;
        } else {
          // Handle regular array data
          const data = response.data as FileUpload[] || [];
          // Sort by filename A-Z by default
          data.sort((a, b) => {
            const filenameA = (a.original_filename || a.filename || '').toLowerCase();
            const filenameB = (b.original_filename || b.filename || '').toLowerCase();
            return filenameA.localeCompare(filenameB, 'de', { numeric: true, sensitivity: 'base' });
          });
          this.dataSource.data = data;
        }
        
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
        this.isRateLimitReached = false;
        
        // Sync paginator state after data loads
        if (this.paginator) {
          this.paginator.pageIndex = this.currentPageIndex;
          this.paginator.pageSize = this.currentPageSize;
        }
        
        // Apply sorting if sort is available
        if (this.sort) {
          // Ensure sort is set to filename A-Z if not already set
          if (!this.sort.active || this.sort.active === '') {
            this.sort.active = 'original_filename';
            this.sort.direction = 'asc';
          }
          this.dataSource.sort = this.sort;
        }
      },
      (error) => {
        console.error('Error loading dokumente for tableName:', this.tableName, 'recordId:', this.recordId, 'Error:', error);
        this.isLoadingResults = false;
        this.isRateLimitReached = true;
        this.snackBar.open('Fehler beim Laden der Dokumente', 'Schließen', { duration: 3000 });
      }
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    // Reset pagination when filtering
    this.currentPageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    
    // Note: If using server-side search, we would call loadDokumente() here
    // For now, client-side filtering is used
  }

  onSearchInput(event: any) {
    const filterValue = event.target.value;
    this.searchValue = filterValue;
    
    // Reset pagination when searching
    this.currentPageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    
    // Use server-side search if search value is provided, otherwise use client-side
    if (filterValue && filterValue.trim().length > 0) {
      // Server-side search
      this.loadDokumente();
    } else {
      // Client-side filtering for empty search
      this.dataSource.filter = '';
      this.loadDokumente();
    }
  }

  clearSearch() {
    this.searchValue = '';
    this.currentPageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.dataSource.filter = '';
    this.loadDokumente();
  }

  onPageChange(event: any) {
    console.log('Page change event:', event);
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.loadDokumente();
  }

  onSortChange() {
    this.loadDokumente();
  }

  editDokument(dokument: FileUpload) {
    console.log('Edit dokument:', dokument);
    // For now, just show a message since we don't have a separate edit page
    this.snackBar.open('Bearbeitung wird in einer zukünftigen Version verfügbar sein', 'Schließen', { duration: 3000 });
  }

  deleteDokument(dokument: FileUpload) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Datei löschen',
        message: 'Datei wird unwiederruflich gelöscht. Sind sie sicher?',
        cancelButton: 'Nein',
        confirmButton: 'Ja',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dokumenteService.deleteDokument(dokument.id).subscribe(
          (response) => {
            this.snackBar.open('Datei erfolgreich gelöscht', 'Schließen', { duration: 3000 });
            this.loadDokumente(); // Reload the data
          },
          (error) => {
            console.error('Error deleting dokument:', error);
            this.snackBar.open('Fehler beim Löschen der Datei', 'Schließen', { duration: 5000 });
          }
        );
      }
    });
  }

  addDokument() {
    console.log('Add new dokument for tableName:', this.tableName, 'recordId:', this.recordId);
    // FilePond upload is handled automatically
  }

  // FilePond event handlers
  pondHandleInit() {
    console.log('FilePond has initialised', this.myPond);
  }

  pondHandleAddFile(event: any) {
    console.log('A file was added', event);
    
    // Additional validation for Plan Dateien context
    if (this.isPlanDateienContext) {
      const file = event.file;
      if (!file) {
        return;
      }
      
      // FilePond FileItem uses fileType/filename, not type/name
      const fileType = file.fileType || file.type || '';
      const fileName = file.filename || file.name || '';
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
      
      // Check if file is JPG or PNG
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      
      const isValidType = allowedTypes.includes(fileType.toLowerCase()) || 
                         allowedExtensions.includes(fileExtension);
      
      if (!isValidType) {
        // Remove the invalid file from FilePond
        if (this.myPond) {
          this.myPond.removeFile(file);
        }
        
        // Show error dialog with single OK button
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Dateityp nicht unterstützt',
            message: 'Nur JPG und PNG Dateien werden unterstützt.',
            cancelButton: null,
            confirmButton: 'OK',
          },
        });
        
        return;
      }
    }
  }

  onProcessFiles(event: any) {
    console.log('Files processed', event);
    // Reload the document list after upload
    this.loadDokumente();
  }

  pondHandleActivateFile(event: any) {
    console.log('A file was activated', event);
  }

  downloadDokument(dokument: FileUpload) {
    console.log('Download dokument:', dokument);
    this.dokumenteService.downloadDokument(dokument.id).subscribe(
      (response) => {
        // Handle file download
        const blob = new Blob([response], { type: dokument.mime_type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = dokument.original_filename;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Dokument wird heruntergeladen', 'Schließen', { duration: 2000 });
      },
      (error) => {
        console.error('Error downloading dokument:', error);
        this.snackBar.open('Fehler beim Herunterladen des Dokuments', 'Schließen', { duration: 5000 });
      }
    );
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video_file';
    if (mimeType.startsWith('audio/')) return 'audio_file';
    if (mimeType.includes('pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'table_chart';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'slideshow';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'folder_zip';
    if (mimeType.startsWith('text/')) return 'text_snippet';
    return 'insert_drive_file';
  }

  // Map MIME to display category
  getDisplayCategory(mimeType: string): string {
    if (!mimeType) return 'Dokument';
    if (mimeType.startsWith('image/')) return 'Bild';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'Kalkulation';
    if (mimeType.includes('word') || mimeType.includes('document') || mimeType.startsWith('text/')) return 'Dokument';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Präsentation';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.startsWith('audio/')) return 'Audio';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'Archiv';
    return 'Dokument';
  }

  showEmbeddedUI(): void {
    console.log('🎯 Showing embedded UI for dokumente component');
    // Hide header elements, adjust styling for embedded view
    // This will be handled in the template with conditional rendering
  }

  showStandaloneUI(): void {
    console.log('🎯 Showing standalone UI for dokumente component');
    // Show full header, navigation, etc. for standalone view
    // This will be handled in the template with conditional rendering
  }

  // Debug method to check JWT token
  checkJwtToken(): void {
    const session = localStorage.getItem('session');
    console.log('JWT Token check:');
    console.log('- Session exists:', !!session);
    
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        const token = sessionData.access_token;
        console.log('- Token exists:', !!token);
        
        if (token) {
          console.log('- Token length:', token.length);
          console.log('- Token preview:', token.substring(0, 50) + '...');
          
          // Try to decode the token (without verification)
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            console.log('- Token payload:', payload);
            console.log('- Token expires:', new Date(payload.exp * 1000));
          } catch (e) {
            console.log('- Could not decode token payload');
          }
        }
        
        console.log('- Full session data:', sessionData);
      } catch (e) {
        console.error('Error parsing session data:', e);
      }
    }
  }

  // Helper method to get standort names for grouped view
  getStandortNames(): string[] {
    return Object.keys(this.groupedDokumente || {}).sort();
  }
}
